import { useCallback, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { actions as dialog } from "../component/dialog"
import ui from "../commons/uiSlice"
import { dialogMessages } from "../i18n/lstrings"
import useConfirmDialog from "../component/dialog/useConfirmDialog"

/**
 * Basically a hook to have a dirty mode with a confirmation dialog before changing page.
 *
 * By default, dirty state is inferred from Redux state.ui.isDirty, if you provide a "isDirty selector"
 * then it will be used instead. Common use case is when you have a Redux-Form,
 * the Redux-Form API already provides a isDirty selector.
 * See: https://redux-form.com/8.3.0/docs/api/selectors.md/#-code-isdirty-formname-string-code-returns-code-state-fields-string-gt-dirty-boolean-code-
 *
 * @param onChangePageHandler a function that will be call when changing page, make sure it is properly wrapped in a #useCallback
 * @param isDirtySelector
 */
export default function useDirtyMode(
  onChangePageHandler,
  isDirtySelector = (state) => state.ui.isDirty
) {
  const dispatch = useDispatch()

  // by default maintain a dirty state in Redux Store
  const isDirty = useSelector(isDirtySelector)

  /**
   * Just make sure dirty is true once form has changed
   */
  useEffect(() => {
    return () => {
      if (isDirty) {
        dispatch(ui.actions.reset())
      }
    }
  }, [dispatch, isDirty])

  /**
   * Basically a hook that will run "onChangePageHandler" when confirmDialog.changePageHandler is true.
   */
  useConfirmDialog("changePageAction", (state) => {
    if (state.changePageAction) {
      onChangePageHandler()
    }
  })

  /**
   * A function that dispatch a dirty event, used when any modification occurred.
   */
  const toDirtyAction = useCallback(() => {
    if (!isDirty) {
      dispatch(ui.actions.toDirty())
    }
  }, [dispatch, isDirty])

  /**
   * A function that will show confirmation dialog when component is dirty.
   */
  const askConfirmation = useCallback(() => {
    if (isDirty) {
      dispatch(
        dialog.show(dialogMessages.dirtyGoBack, {
          title: dialogMessages.dirtyTitle,
          toConfirm: { changePageAction: true }
        })
      )
    } else {
      onChangePageHandler()
    }
  }, [dispatch, isDirty, onChangePageHandler])

  return { isDirty, askConfirmation, toDirty: toDirtyAction }
}
